export const parseConnectionReport = (connectionReport) => {
    const parsedReport = connectionReport?.reports?.All;
    const sumData = parsedReport?.sum_data;

    const sumDataAmended = {
        incomingLeads: sumData?.other_status_count_total, // incoming leads
        outgoingLeads: sumData?.sent_operation_count_total, // outgoing leads
        totalLeads: sumData?.sent_operation_count_total,
    };

    const stats = parsedReport?.lead_stats_singular
        ?.map((stat) => {
            if (stat?.form_name) {
                stat?.page_name && delete stat?.page_name;
                stat?.name && delete stat?.name;
            } else if (stat?.page_name) {
                stat?.form_name && delete stat?.form_name;
                stat?.name && delete stat?.name;
            } else {
                stat?.page_name && delete stat?.page_name;
                stat?.form_name && delete stat?.form_name;
            }
            return stat;
        })
        ?.filter((stat) => stat?.successfully_sent_at);

    // Parse hourly stats for short date ranges (max 2 days)
    const hourlyStats = parsedReport?.hourly_stats;
    const hourlyChartData = parseHourlyStats(hourlyStats);

    return {
        parsedReport,
        sumData: sumDataAmended,
        stats,
        hourlyStats: hourlyChartData,
    };
};

type SourceMappingForm = {
    form_id?: string | number;
    form_name?: string;
};

type SourceMappingPage = {
    page_id?: string | number;
    name?: string;
    forms?: SourceMappingForm[];
};

type SourceMappingSource = {
    type?: string;
    code?: string;
    name?: string;
    form_name?: string;
    pages?: SourceMappingPage[];
};

type SourceMappingDestination = {
    type?: string;
    code?: string;
    name?: string;
};

type SourceMapping = {
    source?: SourceMappingSource;
    destination?: SourceMappingDestination;
};

export type ParsedSourceMapping = {
    sourceType?: string;
    sourceName?: string;
    sourceFormNames: string[];
    sourcePageNames: string[];
    sourceIconCode?: string;
    destinationIconCode?: string;
    destinationName?: string;
    destinationType?: string;
};

export const parseSourceMapping = (
    sourceMapping: SourceMapping | null | undefined,
): ParsedSourceMapping => {
    const source = sourceMapping?.source;
    const destination = sourceMapping?.destination;

    const pages = Array.isArray(source?.pages) ? source!.pages! : [];

    const sourcePageNames = pages
        .map(
            (page) =>
                page?.name ??
                (page?.page_id != null ? String(page.page_id) : ""),
        )
        .filter((name): name is string => !!name);

    const sourceFormNamesFromPages = pages.flatMap((page) =>
        (Array.isArray(page?.forms) ? page!.forms! : [])
            .map(
                (form) =>
                    form?.form_name ??
                    (form?.form_id != null ? String(form.form_id) : ""),
            )
            .filter((name): name is string => !!name),
    );

    // Legacy fallback: API used to return a single `source.form_name`
    const sourceFormNames =
        sourceFormNamesFromPages.length > 0
            ? sourceFormNamesFromPages
            : source?.form_name
              ? [source.form_name]
              : [];

    return {
        sourceType: source?.type,
        sourceName: source?.name,
        sourceFormNames,
        sourcePageNames,
        sourceIconCode: source?.code,
        destinationIconCode: destination?.code,
        destinationName: destination?.name,
        destinationType: destination?.type,
    };
};

/**
 * Parse hourly stats into chart-ready format
 * Input: { "2026-01-05": { "11:00": { sent_operation_count: 8, other_status_count: 9 }, ... } }
 * Output: [{ date: "2026-01-05", hour: "11:00", label: "11:00", count: 8 }, ...]
 */
export const parseHourlyStats = (hourlyStats) => {
    if (!hourlyStats) return null;

    const chartData: Array<{
        date: string;
        hour: string;
        label: string;
        count: number;
    }> = [];

    // Get all dates and sort them
    const dates = Object.keys(hourlyStats).sort();

    dates.forEach((date) => {
        const hoursData = hourlyStats[date];
        if (!hoursData) return;

        // Get all hours and sort them
        const hours = Object.keys(hoursData).sort();

        hours.forEach((hour) => {
            const hourData = hoursData[hour];
            chartData.push({
                date,
                hour,
                label: dates.length > 1 ? `${date.slice(5)} ${hour}` : hour, // Show date prefix for multi-day
                count: hourData?.sent_operation_count || 0, // Only sent_operation_count
            });
        });
    });

    return chartData;
};
